import React from 'react';
import styles from './AboutPage.module.css';

const steps = [
  { icon: '📥', title: 'Capture Network Traffic', desc: 'Open Chrome/Firefox DevTools, go to the Network tab, browse the target site while logged in or logged out, then right-click any request and export all as HAR.' },
  { icon: '📂', title: 'Drop Your HAR File', desc: 'Drag and drop your .har or .json file onto HARScope, or click to browse. The file is read entirely in your browser — zero bytes are sent to any server.' },
  { icon: '🔍', title: 'Analyze Requests', desc: 'All HTTP requests appear instantly. Filter by type (XHR, JS, CSS, Images…), search across URLs, headers and parameters, or use the in-scope filter.' },
  { icon: '🎯', title: 'Set Your Target Domain', desc: 'Type your target (e.g. example.com) and use the In-Scope / External chips to isolate requests going to the target and its subdomains vs. third-party endpoints.' },
  { icon: '📋', title: 'One-Click URL Copy', desc: 'Single-clicking any row instantly copies its full URL to your clipboard and opens the detail panel for deeper inspection.' },
  { icon: '⬇', title: 'Export Filtered URLs', desc: 'Hit "Export URLs" to get a clean plain-text list of all currently visible URLs. Copy to clipboard or download as a .txt file — respects active filters.' },
];

const faqs = [
  { q: 'Are my HAR files uploaded to a server?', a: 'No. HARScope is a 100% client-side application. Your HAR file is read by the browser\'s FileReader API and parsed entirely in local memory. It never leaves your device. There is no backend, no database, and no analytics that touches your file content.' },
  { q: 'What is a HAR file?', a: 'HAR (HTTP Archive) is a JSON-formatted log of a web browser\'s interaction with a site. It records all HTTP requests and responses — URLs, headers, status codes, timings, response bodies, and more. You can export one from Chrome, Firefox, or Edge DevTools.' },
  { q: 'How do I export a HAR file from Chrome?', a: 'Open DevTools (F12) → Network tab → Browse the target page → Right-click any request in the list → "Save all as HAR with content". Then drop it into HARScope.' },
  { q: 'What does "In-Scope" mean?', a: 'When you enter a target domain (e.g. example.com), HARScope color-codes and filters requests: green = in-scope (go to example.com or any subdomain like api.example.com), amber = external (go to third-party services like Google Analytics, CDNs, etc.).' },
  { q: 'Can I use this for bug bounty programs?', a: 'Yes — that\'s exactly what it\'s built for. Capture HAR files for pages you\'re testing (logged in, logged out, profile, cart, etc.), upload them here, and quickly find in-scope API endpoints, sensitive headers, or interesting parameters without sifting through noise.' },
  { q: 'Is this tool free?', a: 'Yes, completely free, forever. No account, no sign-up, no rate limits. It runs entirely in your browser.' },
];

export default function AboutPage() {
  return (
    <div className={styles.page}>
      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroIcon}>🔍</div>
          <h1 className={styles.heroTitle}>About HARScope</h1>
          <p className={styles.heroSub}>
            A privacy-first HAR file viewer and analyzer built for pentesters,
            bug bounty hunters, and web security researchers.
          </p>
          <div className={styles.heroBadges}>
            <span className={styles.badge}>🔒 Zero Upload</span>
            <span className={styles.badge}>⚡ Instant Parse</span>
            <span className={styles.badge}>🆓 Free Forever</span>
            <span className={styles.badge}>🎯 Scope Filtering</span>
          </div>
        </div>
      </section>

      {/* PRIVACY CALLOUT */}
      <section className={styles.section}>
        <div className={styles.privacyBox}>
          <div className={styles.privacyIcon}>🔒</div>
          <div>
            <h2 className={styles.privacyTitle}>Your files never leave your browser</h2>
            <p className={styles.privacyText}>
              HARScope has <strong>no backend server</strong>. When you drop a HAR file, your browser reads it
              using the native <code>FileReader</code> API and parses the JSON entirely in local memory.
              Nothing is uploaded, stored, logged, or transmitted. You can even use HARScope completely
              offline after the page has loaded. This makes it safe to use with sensitive pentesting targets,
              internal applications, and private bug bounty programs.
            </p>
          </div>
        </div>
      </section>

      {/* HOW TO USE */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>How to Use HARScope</h2>
        <div className={styles.stepsGrid}>
          {steps.map((s, i) => (
            <div className={styles.stepCard} key={i}>
              <div className={styles.stepNum}>{i + 1}</div>
              <div className={styles.stepIcon}>{s.icon}</div>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Key Features</h2>
        <div className={styles.featGrid}>
          {[
            ['🎯', 'In-Scope / External Filtering', 'Enter your target domain and instantly isolate requests going to it and its subdomains vs. third-party noise.'],
            ['📋', 'One-Click URL Copy', 'Single click any row to auto-copy its full URL — no need to open the detail panel manually.'],
            ['🔍', 'Deep Search', 'Search across URLs, request headers, and query parameters in real time.'],
            ['📊', 'Type Filters', 'Filter by XHR/Fetch, JS, CSS, Images, Media, Fonts, Documents, or Errors.'],
            ['📄', 'Full URL Display', 'See the complete URL in the table — domain highlighted in color, path visible at a glance.'],
            ['⬇', 'Smart URL Export', 'Export only the URLs visible under your current filters. Copy to clipboard or download as .txt.'],
            ['🔎', 'Request Inspector', 'Inspect request/response headers, body (JSON pretty-printed), and query params in the detail panel.'],
            ['⚡', 'Instant & Offline', 'Pure client-side parsing — no loading spinners, no servers, works offline.'],
          ].map(([icon, title, desc]) => (
            <div className={styles.featCard} key={title}>
              <div className={styles.featIcon}>{icon}</div>
              <h3 className={styles.featTitle}>{title}</h3>
              <p className={styles.featDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
        <div className={styles.faqList}>
          {faqs.map((f, i) => (
            <div className={styles.faqItem} key={i}>
              <h3 className={styles.faqQ}>{f.q}</h3>
              <p className={styles.faqA}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TECH */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Built With</h2>
        <p className={styles.techText}>
          HARScope is built with <strong>React</strong>, deployed on <strong>Vercel</strong>,
          and styled with CSS Modules. The font stack uses <strong>JetBrains Mono</strong> (code/data)
          and <strong>Syne</strong> (headings). There are zero tracking pixels, zero analytics, and zero
          third-party scripts that can see your data.
        </p>
      </section>
    </div>
  );
}
